<template>
  <v-container>
    <div class="d-flex align-center mb-4">
      <h2 class="text-h6 mb-0">
        給与確定
      </h2>
      <v-spacer />
      <v-btn
        color="primary"
        @click="openCreateDialog"
      >
        <v-icon left>
          {{ icons.mdiPlus }}
        </v-icon>
        新規作成
      </v-btn>
    </div>

    <ConfirmationList
      :groups="groups"
      :is-loading="isLoadingGroups"
      :role-label="roleLabel"
      @delete="onDeleteGroup"
    />

    <!-- 新規作成ダイアログ -->
    <v-dialog
      v-model="createDialog"
      max-width="760"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          確定データを新規作成
        </v-card-title>
        <v-card-text>
          <ConfirmationFilters
            :role="createRole"
            :role-options="roleOptions"
            :is-loading-roles="isLoadingRoles"
            :dates="createDates"
            :is-valid-selection="isCreateValid"
            :is-building="isBuilding"
            hide-build-button
            @update:role="createRole = $event"
            @update:dates="createDates = $event"
          />

          <v-alert
            v-if="existingGroupWarning"
            type="warning"
            text
            dense
            class="mt-4 mb-0"
          >
            同じロール×期間の確定データが既に存在します。作成すると下書きが再生成されます（手動編集・確定済みのセルは保持されます）。
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="isBuilding"
            @click="createDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!isCreateValid || isBuilding"
            :loading="isBuilding"
            @click="onCreate"
          >
            確定データを作成
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  onMounted, ref, computed, getCurrentInstance,
} from '@vue/composition-api'
import { mdiPlus } from '@mdi/js'
import ConfirmationFilters from './components/ConfirmationFilters.vue'
import ConfirmationList from './components/ConfirmationList.vue'
import { usePayrollConfirmation } from './composables/usePayrollConfirmation'

export default {
  components: {
    ConfirmationFilters,
    ConfirmationList,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const {
      roleOptions,
      selectedRole,
      dates,
      groups,
      isLoadingRoles,
      isLoadingGroups,
      isBuilding,
      roleLabel,
      fetchRoles,
      fetchGroups,
      buildConfirmations,
      deleteDrafts,
    } = usePayrollConfirmation()

    // 新規作成ダイアログの状態
    const createDialog = ref(false)
    const createRole = ref(null)
    const createDates = ref([])

    const sortedCreateDates = computed(() => {
      const valid = createDates.value.filter(d => !!d)
      if (valid.length !== 2) return null

      return [...valid].sort()
    })

    const isCreateValid = computed(() => !!createRole.value && !!sortedCreateDates.value)

    // 同じロール×期間が既にあるか（再生成の注意喚起用）
    const existingGroupWarning = computed(() => {
      if (!isCreateValid.value) return false
      const [start, end] = sortedCreateDates.value

      return groups.value.some(
        g => g.role === createRole.value && g.periodStart === start && g.periodEnd === end,
      )
    })

    const onDeleteGroup = async group => {
      await deleteDrafts(group)
    }

    const openCreateDialog = () => {
      createRole.value = null
      createDates.value = []
      createDialog.value = true
    }

    // 新規作成: 選択値で確定データを作成し、成功したらその期間の詳細ページへ遷移する
    const onCreate = async () => {
      const [start, end] = sortedCreateDates.value
      selectedRole.value = createRole.value
      dates.value = [start, end]
      const ok = await buildConfirmations()
      if (!ok) return

      createDialog.value = false
      vm.$router.push({
        name: 'payroll-confirmation-detail',
        params: {
          club_slug: vm.$route.params.club_slug,
          role: createRole.value,
          periodStart: start,
          periodEnd: end,
        },
      })
    }

    onMounted(() => {
      fetchRoles()
      fetchGroups()
    })

    return {
      roleOptions,
      groups,
      isLoadingRoles,
      isLoadingGroups,
      isBuilding,
      roleLabel,
      createDialog,
      createRole,
      createDates,
      isCreateValid,
      existingGroupWarning,
      onDeleteGroup,
      openCreateDialog,
      onCreate,
      icons: {
        mdiPlus,
      },
    }
  },
}
</script>
